import { useCallback, useRef, useState } from 'react';
import { useSnackbars } from '@customerio/pluma-components/react';
import {
  Authenticators,
  type GoogleOneClickMustSignupError,
  isGoogleOneClickMustChooseUserAccountError,
  isGoogleOneClickMustSignupError,
  isTwoFactorAuthLinkRequiredError,
  isTwoFactorAuthRequiredError,
} from '@customerio/hydra-authentication';
import { isUnrecognizedLoginError, loginErrorMessage } from '@/services/auth/login-error-message';
import { session } from '@/services/session';
import { openGoogleOIDCPopup } from '@/services/oauth';
import { getGaClientId } from '@/utils/ga-client-id';
import { setTwoFactorAuthLinkCookie } from '@/utils/two-factor-link-cookie';
import { hardRedirect } from '@/utils/hard-redirect';
import { sentryCaptureException } from '@/utils/sentry';

/**
 * `idle` → `running` while the popup and exchange are open → `succeeded` once
 * the Google identity is known and the visitor is finishing on this step. The
 * two are mutually exclusive, so a single value rather than a pair of booleans.
 */
export type GoogleSignupState = 'idle' | 'running' | 'succeeded';

/** The Google identity, for the caller to write into flow and form state. */
export type GoogleSignupIdentity = {
  email: string;
  firstName: string;
  lastName: string;
};

export type UseGoogleOneClickSignupOptions = {
  /**
   * Called with the Google identity before the funnel advances. The caller owns
   * writing it into both the flow state and the form.
   */
  onIdentity: (identity: GoogleSignupIdentity) => void;
  /**
   * Continue the funnel. Only called when Google returned a complete name —
   * otherwise the visitor stays here and submits the form themselves.
   */
  onContinue: (email: string) => Promise<void>;
};

/**
 * Routes a Google identity that already maps to a Customer.io account back to
 * login. The chooser and 2FA-code states live in memory and can't cross pages,
 * so those hand off to `/login` for the visitor to re-initiate; the 2FA email
 * link rides in a cookie login already reads.
 *
 * Returns whether the error was a login scenario (and has been handled).
 */
function routeToLoginIfAlreadyRegistered(error: unknown): boolean {
  if (isTwoFactorAuthLinkRequiredError(error)) {
    setTwoFactorAuthLinkCookie(error.twoFactorAuthLinkToken);
    hardRedirect('/login/2fa_email_link');
    return true;
  }

  if (isGoogleOneClickMustChooseUserAccountError(error) || isTwoFactorAuthRequiredError(error)) {
    hardRedirect('/login');
    return true;
  }

  return false;
}

/** Open the Google popup and exchange the code for a session. */
async function runGoogleExchange(): Promise<void> {
  const { authorizationCode, redirectUri } = await openGoogleOIDCPopup();
  await session.authenticate(Authenticators.GoogleOneClickSignup, {
    code: authorizationCode,
    redirect_uri: redirectUri,
    ga_client_id: getGaClientId(),
    // The funnel is never an add-account flow.
    add_session: false,
  });
}

/** Google can omit either name, in which case the visitor supplies it later. */
function toIdentity(error: GoogleOneClickMustSignupError): GoogleSignupIdentity {
  return {
    email: error.usersData.googleAccountEmail,
    firstName: error.usersData.first ?? '',
    lastName: error.usersData.last ?? '',
  };
}

/**
 * Google One-Click sign-up.
 *
 * ⚠️ The sign-up path is the **catch** branch. A resolved `authenticate` means
 * the Google identity already maps to an account and the visitor has been
 * logged in; it's `GoogleOneClickMustSignupError` — thrown on a 200 — that
 * carries the new user's name and email and means "carry on signing up".
 */
export function useGoogleOneClickSignup({ onIdentity, onContinue }: UseGoogleOneClickSignupOptions) {
  const snackbars = useSnackbars();
  const [state, setState] = useState<GoogleSignupState>('idle');
  // Synchronous re-entry guard: `state` flushes a render late, so two clicks
  // landing in the same tick would both open a popup.
  const isRunningRef = useRef(false);

  // Shares login's mapper: this authenticator throws `UnknownLoginError` for
  // every non-OK response and the popup throws `OAuthError`, so a `LoginError`
  // check would send 401s, 429s, attempts-limit and a dismissed popup all to
  // the generic copy and an error-level Sentry report.
  const showError = useCallback(
    (error: unknown) => {
      if (isUnrecognizedLoginError(error)) {
        sentryCaptureException(new Error('Unknown google one-click signup error'), {
          level: 'error',
          extra: { originalError: error },
        });
      }
      snackbars.create({ variant: 'critical', id: 'login.error', message: loginErrorMessage(error) });
    },
    [snackbars],
  );

  const continueSignup = useCallback(
    async (error: GoogleOneClickMustSignupError) => {
      const identity = toIdentity(error);
      onIdentity(identity);

      // Google didn't give us a full name, so the visitor finishes here: the
      // step keeps the success banner and its own submit continues the funnel.
      if (identity.firstName === '' || identity.lastName === '') {
        setState('succeeded');
        return;
      }

      try {
        await onContinue(identity.email);
      } catch {
        // `onContinue` owns reporting its own failure. Land on `succeeded` so
        // the visitor can retry with the submit button — reverting to `idle`
        // would re-enable the email field while the signup is still bound to
        // the Google address, letting a different one skip verification.
        setState('succeeded');
      }
    },
    [onIdentity, onContinue],
  );

  const handleFailure = useCallback(
    (error: unknown) => {
      setState('idle');
      if (!routeToLoginIfAlreadyRegistered(error)) {
        showError(error);
      }
    },
    [showError],
  );

  const runFlow = useCallback(async () => {
    try {
      await runGoogleExchange();
      // Resolved: the identity already had an account and login took over.
      setState('idle');
    } catch (error) {
      if (isGoogleOneClickMustSignupError(error)) {
        await continueSignup(error);
      } else {
        handleFailure(error);
      }
    } finally {
      isRunningRef.current = false;
    }
  }, [continueSignup, handleFailure]);

  // Synchronous, and reports whether this call is the one that began: the
  // caller's click analytics must fire once per attempt, at click time, so it
  // can neither wait for the flow nor double-count a suppressed second click.
  const start = useCallback((): boolean => {
    if (isRunningRef.current) {
      return false;
    }
    isRunningRef.current = true;
    setState('running');
    void runFlow();
    return true;
  }, [runFlow]);

  return { state, start };
}
